import { defineStore } from 'pinia'
import { ref, inject } from 'vue'
import { FeatureFlagEvaluation } from '@/types'
import { launchDarklyInjectionKey } from '@/utils/launchDarklyClient'
import type * as LaunchDarkly from 'launchdarkly-js-client-sdk'

export const useFeatureFlagStore = defineStore('FeatureFlagStore', () => {
  const ldClient = inject(launchDarklyInjectionKey)

  if (!ldClient) {
    throw new Error('LaunchDarkly client not provided. Make sure it is initialized in main.ts')
  }

  const isReady = ref(false)
  const flags = ref<FeatureFlagEvaluation[]>([])

  const getFlag = (key: string) => flags.value?.find((flag: FeatureFlagEvaluation) => flag.id === key)

  ldClient.on('ready', () => (isReady.value = true))

  const fetchFlags = async () => {
    try {
      await ldClient.waitForInitialization(5)

      if (import.meta.env.VITE_OUTPUT_ENVIRONMENT !== 'production') {
        console.log('LD Client Initialized with Context:', ldClient.getContext())
      }

      if (flags.value?.length > 0) {
        return
      }

      const allFlags = ldClient.allFlags()

      setFlags(allFlags)
    } catch (err) {
      // LD took too long to respond
    }
  }

  const setFlags = (flagsResponse: LaunchDarkly.LDFlagSet) => {
    const flagEvaluations = Object.entries(flagsResponse).map(([key, value]) => {
      return {
        id: key,
        value
      }
    })

    flags.value = [...flagEvaluations]
  }

  const setUserContext = async (outputId: string) => {
    const ldContext = ldClient.getContext() as LaunchDarkly.LDMultiKindContext
    const { kind, ...currentContext } = ldContext

    const userContext = {
      kind: 'user',
      key: outputId,
      anonymous: false
    }

    const context = {
      kind: 'multi',
      ...currentContext,
      user: userContext
    }

    const identifiedFlags = await ldClient.identify(context)

    setFlags(identifiedFlags)
  }

  return {
    fetchFlags,
    flags,
    getFlag,
    isReady,
    setUserContext
  }
})
